import { Box } from "@mui/material";
import { SuspenseFallback } from "common/components/LoadingScreen";
import InitialSelectScreen from "features/initialSelectScreen/InitialSelectScreen";
import { UpdateBiomassAfterMoveInDialog } from "features/singleCoverInput";
import { MutableRefObject, ReactNode, Suspense, useMemo } from "react";
import { MapRef } from "react-map-gl/mapbox";
import { useLocation } from "react-router-dom";
import { drawerWidth, secondaryPanelWidth } from "width";
import {
  findRouteByPath,
  getLayoutStateForPath,
  shouldShowMap,
} from "../routes";

interface DesktopLayoutProps {
  children: ReactNode;
  mapRef: MutableRefObject<MapRef | null>;
}

/**
 * Desktop layout component with fixed drawer sidebar and content panel.
 * The drawer (nav sidebar) is always visible at fixed width.
 * The content panel shows route-specific content.
 * Map is rendered by AppLayout to the right of this component.
 */
export default function DesktopLayout({
  children,
  mapRef,
}: DesktopLayoutProps) {
  const location = useLocation();
  const showMap = shouldShowMap(location.pathname);

  // Get layout state from route config
  const layoutState = useMemo(
    () => getLayoutStateForPath(location.pathname),
    [location.pathname]
  );
  const { isMapRoute } = layoutState;

  // Check if this route has special desktop handling (renders its own content panel)
  const route = useMemo(
    () => findRouteByPath(location.pathname),
    [location.pathname]
  );
  const hasSpecialDesktopHandling = route?.hasSpecialDesktopHandling ?? false;

  // Drawer background color - green for most routes, transparent for map routes
  const drawerBackgroundColor = isMapRoute ? "transparent" : "#1C4321";

  return (
    <>
      {/* Navigation sidebar - always visible, fixed width */}
      <Box
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          height: "100vh",
          backgroundColor: drawerBackgroundColor,
          borderRight: "1px solid #e4e4e7",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <UpdateBiomassAfterMoveInDialog />
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <InitialSelectScreen />
        </Box>
      </Box>

      {/* Content panel - width depends on whether map is shown */}
      {/* Routes with special desktop handling render their own fixed-position content panel */}
      {hasSpecialDesktopHandling ? (
        // For special handling routes, just render children without a wrapper panel
        // The children (DesktopContent) will render the fixed-position content panels
        <Suspense fallback={<SuspenseFallback />}>{children}</Suspense>
      ) : (
        <Box
          sx={{
            width: showMap ? secondaryPanelWidth : undefined,
            flex: showMap ? "0 0 auto" : 1,
            height: "100vh",
            overflow: "hidden",
            backgroundColor: "white",
            // Rounded corners and shadow on the right edge when map is shown
            ...(showMap && {
              borderRadius: "0 16px 16px 0",
              boxShadow:
                "4px 0 8px rgba(0, 0, 0, 0.08), 8px 0 16px rgba(0, 0, 0, 0.05)",
              // Ensure panel is above the map (which has zIndex ~1199) so rounded corners are visible
              zIndex: 1300,
              position: "relative",
            }),
          }}
        >
          <Box
            sx={{
              height: "100%",
              overflow: "auto",
              // Subtle scrollbar styling
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(0, 0, 0, 0.1)",
                borderRadius: "4px",
                transition: "background 0.2s ease",
              },
              "&:hover::-webkit-scrollbar-thumb": {
                background: "rgba(0, 0, 0, 0.3)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(0, 0, 0, 0.4)",
              },
              // Firefox scrollbar styling
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(0, 0, 0, 0.1) transparent",
              "&:hover": {
                scrollbarColor: "rgba(0, 0, 0, 0.3) transparent",
              },
            }}
          >
            <Suspense fallback={<SuspenseFallback />}>{children}</Suspense>
          </Box>
        </Box>
      )}
    </>
  );
}
